import { BaseResponse } from 'domain/payload/response/BaseResponseDto';
import axiosInstance from '../config/AxiosConfig';
import { UserListResp } from 'domain/payload/response/user/UserListResp';
import { UserRoleResp } from 'domain/payload/response/user/UserRoleResp';
import { UserConfigResp } from 'domain/payload/response/user/UserConfigResp';
import { UserDetailResp } from 'domain/payload/response/user/UserDetailResp';
import { UserCreateReq } from 'domain/payload/request/user/UserCreateReq';
import { UserUpdateReq } from 'domain/payload/request/user/UserUpdateReq';
import { UserSuspensionDetailResp } from 'domain/payload/response/user/UserSuspensionDetailResp';
import { UserSuspensionProceedReq } from 'domain/payload/request/user/UserSuspensionProceed';
import { BackOfficeTrailActivityDetailResp, BackOfficeTrailDetailResp } from 'domain/payload/response/backOfficeTrails/BackOfficeTrailsResp';

const UserService = () => {
  const userList = async (
    token: string,
    search: string | null,
    page: number | null,
    size: number | null,
    roleId?: string | null
  ): Promise<BaseResponse<UserListResp[]>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/user/list`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { search, page, size, roleId },
      });
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || 'Failed to fetch user list';
    }
  };

  const userDetail = async (
    token: string,
    id: string
  ): Promise<BaseResponse<UserDetailResp>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/user/detail`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { id },
      });
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || 'Failed to fetch user detail';
    }
  };

  const userExportToCsv = async (token: string): Promise<BaseResponse<any>> => {
    try {
      const response = await axiosInstance.post(
        `/v1/legacy/cms/report-engine/download?reportCode=USER_CSV_EXPORT&extension=csv`,
        {},
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || error.message || error;
    }
  };

  const userInvite = async (
    token: string,
    request: UserCreateReq
  ): Promise<BaseResponse<UserDetailResp>> => {
    try {
      const response = await axiosInstance.post(`/v1/legacy/cms/user/invite`, request, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || 'Failed to invite user';
    }
  };

  const userUpdate = async (
    token: string,
    request: UserUpdateReq
  ): Promise<BaseResponse<UserDetailResp>> => {
    try {
      const response = await axiosInstance.put(`/v1/legacy/cms/user/update`, request, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || 'Failed to update user';
    }
  };

  const userDelete = async (token: string): Promise<BaseResponse<any>> => {
    try {
      const response = await axiosInstance.delete(`/v1/legacy/cms/user/delete`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || 'Failed to delete user';
    }
  };

  const userRole = async (
    token: string
  ): Promise<BaseResponse<UserRoleResp[]>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/rbac/role-list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || 'Failed to fetch roles';
    }
  };

  const userConfig = async (
    token: string
  ): Promise<BaseResponse<UserConfigResp>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/user/config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || 'Failed to fetch user config';
    }
  };

  const userSuspensionDetail = async (
    token: string,
    userId: string
  ): Promise<BaseResponse<UserSuspensionDetailResp>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/suspension/detail`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { userId },
      });
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || 'Failed to fetch user detail';
    }
  };

  const userSuspensionProceed = async (
    token: string,
    request: UserSuspensionProceedReq
  ): Promise<BaseResponse<any>> => {
    try {
      const response = await axiosInstance.post(`/v1/legacy/cms/suspension/proceed`, request, {
        headers: {
          Authorization: `Bearer ${token}`
        },
      });

      return response.data

    } catch (error: any) {
      throw error.response.data.message;
    }
  };

  const userTrailDetail = async (
    token: string,
    id: string
  ): Promise<BaseResponse<BackOfficeTrailDetailResp>> => {
    try {
      const response = await axiosInstance.get(`v1/legacy/cms/user/basic-info`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { id },
      });
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || error.message;
    }
  };

  const userTrailActivityList = async (
    token: string,
    page?: number,
    size?: number,
    search?: string | null,
    startDate?: string | null,
    endDate?: string | null,
    activityType?: string | null,
    id?: string | null
  ): Promise<BaseResponse<BackOfficeTrailActivityDetailResp[]>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/user/activities`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page, size, search, startDate, endDate, activityType, id },
      });
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || error.message;
    }
  };

  return {
    userList,
    userDetail,
    userExportToCsv,
    userInvite,
    userUpdate,
    userDelete,
    userRole,
    userConfig,
    userSuspensionDetail,
    userSuspensionProceed,
    userTrailDetail,
    userTrailActivityList
  };
};

export default UserService;